import { useTranslation } from 'react-i18next'
import utility from '../../../../../shared/helpers/utility'
import GedcomX from '../../../../../shared/helpers/gedcom_X'

const gcx = GedcomX()

export const DEATH_OF_PARENT = 'death of parent'
export const DEATH_OF_PARENT_1 = `${DEATH_OF_PARENT} 1`
export const DEATH_OF_PARENT_2 = `${DEATH_OF_PARENT} 2`

// Constants for translation key modifiers that are repeated
const GENDER_UNKNOWN = 'unknown'
const IN_LOCATION = '.in.location'
const AGE = '.age'

// Dependencies are "built" and injected into translation handlers at runtime
const dependencyBuilders = {
  gender: ({ personInfo }) =>
    personInfo.gender !== 'male' && personInfo.gender !== 'female' ? GENDER_UNKNOWN : personInfo.gender,
  birthDate: ({ personInfo }) => getTranslationDateType(personInfo.birthDate),
  // How an array response from a dependency builder is handled is documented in buildHandlerDependency
  // Basically, the final dependency translation string is the second element unless the first element is an empty string
  birthLocation: ({ translationOptions }) => [translationOptions.birthLocation, IN_LOCATION],
  christeningDate: ({ translationOptions }) => getTranslationDateType(translationOptions.christeningDate),
  christeningLocation: ({ translationOptions }) => [translationOptions.christeningLocation, IN_LOCATION],
  residenceDate: ({ translationOptions }) => getTranslationDateType(translationOptions.residenceDate),
  residenceLocation: ({ translationOptions }) => [translationOptions.residenceLocation, IN_LOCATION],
  deathDate: ({ translationOptions }) => getTranslationDateType(translationOptions.deathDate),
  deathLocation: ({ translationOptions }) => [translationOptions.deathLocation, IN_LOCATION],
  deathAge: ({ translationOptions }) => [translationOptions.deathAge, AGE],
  burialDate: ({ translationOptions }) => getTranslationDateType(translationOptions.burialDate),
  burialLocation: ({ translationOptions }) => [translationOptions.burialLocation, IN_LOCATION],
  age: ({ ageAtEvent }) => [ageAtEvent, AGE],
  parents: ({ translationOptions }) => {
    if (translationOptions.parent1 && translationOptions.parent2) {
      return '.both.parents'
    }

    if (translationOptions.parent1 && !translationOptions.parent2) {
      return '.parent1'
    }

    if (!translationOptions.parent1 && translationOptions.parent2) {
      return '.parent2'
    }
    return ''
  },
}

// For each handler:
// - `dependencies` is an array of strings specifying keys in dependencyBuilders
// - `handler` is a callable that takes an object argument containing computed dependencies and returns:
//   - a translation key string (default translation options will be used)
//   - an object containing `key` (a translation key) and `options` (custom translation options)
//
// Several dependencies are available in addition to those defined in the dependencies of a handler:
// - `personInfo` and `translationOptions` always
// - `noInfo`, if the length of a handler's dependencies > 0
const eventDescriptionTranslationHandlers = {
  [gcx.BIRTH]: {
    handler: (deps) =>
      `wcf.person-timeline.ancestor-birth${deps.noInfo}${deps.birthDate}${deps.birthLocation}${deps.parents}.gender-${deps.gender}`,
    dependencies: ['birthDate', 'birthLocation', 'parents', 'gender'],
  },
  [gcx.CHRISTENING]: {
    handler: (deps) =>
      `wcf.person-timeline.ancestor-christening${deps.noInfo}${deps.christeningDate}${deps.christeningLocation}${deps.age}${deps.parents}.gender-${deps.gender}`,
    dependencies: ['christeningDate', 'christeningLocation', 'age', 'parents', 'gender'],
  },
  [gcx.RESIDENCE]: {
    handler: (deps) =>
      `wcf.person-timeline.residence${deps.noInfo}${deps.residenceDate}${deps.residenceLocation}${deps.age}${deps.parents}.gender-${deps.gender}`,
    dependencies: ['residenceDate', 'residenceLocation', 'age', 'parents', 'gender'],
  },
  [DEATH_OF_PARENT_1]: {
    handler: (deps) => deathOfParentEventHandler(deps, DEATH_OF_PARENT_1),
    dependencies: [],
  },
  [DEATH_OF_PARENT_2]: {
    handler: (deps) => deathOfParentEventHandler(deps, DEATH_OF_PARENT_2),
    dependencies: [],
  },
  [gcx.DEATH]: {
    handler: (deps) =>
      `wcf.person-timeline.ancestor-death${deps.noInfo}${deps.deathDate}${deps.deathLocation}.gender-${deps.gender}`,
    dependencies: ['deathDate', 'deathLocation', 'gender'],
  },
  [gcx.BURIAL]: {
    handler: (deps) =>
      `wcf.person-timeline.ancestor-burial${deps.noInfo}${deps.burialDate}${deps.burialLocation}${deps.parents}.gender-${deps.gender}`,
    dependencies: ['burialDate', 'burialLocation', 'parents', 'gender'],
  },
}

export default function useTimelineEventDescription(personInfo, eventType, ageAtEvent) {
  const [t] = useTranslation()
  return getTimelineEventDescription(personInfo, eventType, ageAtEvent, t)
}

// Not a hook so t can be passed in
export function getTimelineEventDescription(personInfo, eventType, ageAtEvent, t) {
  const translationOptions = buildTranslationOptions(personInfo, ageAtEvent)

  return handleTimelineEventDescriptionTranslation(eventType, translationOptions, personInfo, t)
}

export function getTranslationDateType(value) {
  return !Number.isNaN(Number(value)) ? `.general.date` : `.exact.date`
}

// Returns a mapping for substitution in translation strings
// This mapping will be passed into react-i18next's translation function along with the translation key
//
// An example is this translation string in src/locales/en/translation.json:
//
// "wcf.person-timeline.ancestor-birth.no.info.gender-male": "{ancestorName} was born.",
//
// If ancestorName in this mapping is "Steve", this translation maps to "Steve was born."
function buildTranslationOptions(
  {
    birthDate,
    birthLocation,
    burialDate,
    burialLocation,
    christeningDate,
    christeningLocation,
    deathAge,
    deathDate,
    deathLocation,
    name,
    parent1,
    parent2,
    residenceDate,
    residenceLocation,
    surname,
  },
  ageAtEvent
) {
  const ancestorName = name || surname

  return {
    ancestorName,
    birthDate,
    birthLocation,
    birthAge: ageAtEvent,
    christeningDate,
    christeningLocation,
    residenceDate,
    residenceLocation,
    age: ageAtEvent,
    parent1Name: getName(parent1),
    parent2Name: getName(parent2),
    deathDate,
    deathLocation,
    deathAge,
    burialDate,
    burialLocation,
    // Documented here:
    // https://www.i18next.com/translation-function/essentials#overview-options
    // The value to return if no translation with a specified key could be found. Handlers can override this.
    defaultValue: '',
  }
}

function getParentForDeathOfParentEventType(personInfo, eventType) {
  switch (eventType) {
    case DEATH_OF_PARENT_1:
      return personInfo.parent1
    case DEATH_OF_PARENT_2:
      return personInfo.parent2
    default:
      // No reason this should ever happen because eventType is defined statically in both usages of
      // deathOfParentEventHandler, the only consumer of this function
      return null
  }
}

function deathOfParentEventHandler(deps, eventType) {
  const parent = getParentForDeathOfParentEventType(deps.personInfo, eventType)

  const parentGender = parent.gender !== 'male' && parent.gender !== 'female' ? 'unknown' : parent.gender
  const parentTranslationOptions = {
    parentName: getName(parent),
    parentDeathDate: parent?.deathDate !== '' ? parent.deathDate : '',
    parentDeathLocation: parent?.deathLocation !== '' ? parent.deathLocation : '',
    parentDeathAge: parent?.deathAge !== '' ? parent.deathAge : '',
  }

  const date = parent?.deathDate !== '' ? getTranslationDateType(parent.deathDate) : ''
  const location = parent?.deathLocation !== '' ? '.in.location' : ''
  const age = parent?.deathAge !== '' ? '.age' : ''
  const noInfo = getNoInfoString(parentTranslationOptions.parentDeathDate, location, age)

  return {
    key: `wcf.person-timeline.death-of-parent${noInfo}${date}${location}${age}.gender-${parentGender}`,
    options: { ...deps.translationOptions, ...parentTranslationOptions },
  }
}

function buildHandlerDependency(debugName, dependencyBuilder, dependencyDependencies) {
  const builderResult = dependencyBuilder(dependencyDependencies)

  // If array is returned from lambda in this array, computed key is second element a_1 if first element a_0 !== '',
  // otherwise key is ''
  // Any other than the first two elements are ignored
  if (Array.isArray(builderResult)) {
    return builderResult[0] !== '' ? builderResult[1] : ''
  }

  return builderResult
}

function buildHandlerDependencies(dependencies, personInfo, translationOptions) {
  if (!dependencies) {
    return {}
  }

  const dependencyDependencies = {
    personInfo,
    translationOptions,
    ageAtEvent: translationOptions.age,
  }

  const handlerDependencies = Object.assign(
    {},
    ...dependencies.map((dep) => ({
      [dep]: buildHandlerDependency(dep, dependencyBuilders[dep], dependencyDependencies),
    }))
  )

  handlerDependencies.noInfo = getNoInfoString(Object.values(handlerDependencies))

  return handlerDependencies
}

function handleTimelineEventDescriptionTranslation(eventType, translationOptions, personInfo, t) {
  if (!(eventType in eventDescriptionTranslationHandlers)) {
    return null
  }

  const { handler, dependencies: handlerDependencies } = eventDescriptionTranslationHandlers[eventType]

  const genericDependencies = {
    // personInfo and translationOptions are available for all handlers
    personInfo,
    translationOptions,
  }

  const dependencies = {
    // The reason we don't pass genericDependencies in with handlerDependencies to buildHandlerDependencies
    // is because they aren't included in the noInfo check
    ...genericDependencies,
    ...buildHandlerDependencies(handlerDependencies, personInfo, translationOptions),
  }

  const handlerResult = handler(dependencies)

  // If the handler returns an object, as it does in deathOfParentEventHandler, it provides its own translation options
  if (typeof handlerResult === 'object') {
    return t(handlerResult.key, handlerResult.options)
  }

  return t(handlerResult, translationOptions)
}

function allEmptyStrings(...items) {
  return utility.allMeetCondition((item) => item === '', ...items)
}

function getNoInfoString(...itemsToCheck) {
  return allEmptyStrings(...itemsToCheck) ? '.no.info' : ''
}

function getName(person) {
  let name = ''
  if (person && person.name) {
    name = person.name
  } else if (person && person.surname) {
    name = person.surname
  }
  return name
}
